import { useTranslation } from "react-i18next"

import type { LibraryEditor } from "./use-library-editor"

import { Search } from "@/components/icons"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { EditorSectionCard, FieldRow, SwitchRow } from "./editor-fields"

type IndexingSectionProps = {
  editor: LibraryEditor
  embeddingModels: Array<{ id: string; displayName: string }>
}

export function IndexingSection({ editor, embeddingModels }: IndexingSectionProps) {
  const { t } = useTranslation()
  const { values, errors, mode } = editor
  const isFilth = values.search_backend === "filth"
  const locked = mode === "edit"
  const lockedHelp = t(
    "adminDashboard.libraries.editor.setAtCreation",
    "Set at creation. Changing it would orphan the indexed data; recreate the library instead."
  )
  const embeddingLabel =
    embeddingModels.find((model) => model.id === values.embedding_model_id)?.displayName ??
    values.embedding_model_id

  return (
    <EditorSectionCard
      icon={<Search className="size-4" />}
      id="library-indexing"
      open={editor.openSections.indexing}
      title={t("adminDashboard.libraries.editor.sections.indexing", "Indexing and search")}
      onOpenChange={(open) => editor.setSectionOpen("indexing", open)}
    >
      {!isFilth && (
        <FieldRow
          error={errors.embedding_model_id}
          help={locked ? lockedHelp : undefined}
          id="embedding_model_id"
          label={t("adminDashboard.libraries.editor.embeddingModel", "Embedding model")}
          required={!locked}
        >
          {locked ? (
            <Input
              disabled
              className="bg-muted text-muted-foreground"
              id="embedding_model_id"
              value={embeddingLabel}
            />
          ) : (
            <Select
              value={values.embedding_model_id}
              onValueChange={(value) => editor.update({ embedding_model_id: value })}
            >
              <SelectTrigger id="embedding_model_id">
                <SelectValue
                  placeholder={t(
                    "adminDashboard.libraries.editor.embeddingModelPlaceholder",
                    "Select embedding model"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {embeddingModels.length === 0 ? (
                  <SelectItem disabled value="none">
                    {t(
                      "adminDashboard.libraries.editor.noEmbeddingModels",
                      "No embedding models available"
                    )}
                  </SelectItem>
                ) : (
                  embeddingModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.displayName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </FieldRow>
      )}

      <FieldRow
        help={locked ? lockedHelp : undefined}
        id="index_name"
        label={t("adminDashboard.libraries.editor.indexName", "Search index name")}
      >
        <Input
          className={locked ? "bg-muted text-muted-foreground" : undefined}
          disabled={locked}
          id="index_name"
          placeholder={values.id || "library-id"}
          value={values.index_name}
          onChange={(event) => editor.update({ index_name: event.target.value })}
        />
      </FieldRow>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FieldRow
          error={errors.output_container}
          id="output_container"
          label={t("adminDashboard.libraries.editor.outputContainer", "Output container")}
        >
          <Input
            className={locked ? "bg-muted text-muted-foreground" : undefined}
            disabled={locked}
            id="output_container"
            placeholder={`${values.id || "library"}-output`}
            value={values.output_container}
            onChange={(event) =>
              editor.update({ output_container: event.target.value.toLowerCase() })
            }
          />
        </FieldRow>
        <FieldRow
          error={errors.output_table}
          id="output_table"
          label={t("adminDashboard.libraries.editor.outputTable", "Output table")}
        >
          <Input
            className={locked ? "bg-muted text-muted-foreground" : undefined}
            disabled={locked}
            id="output_table"
            placeholder={`${(values.id || "library").replace(/-/g, "")}index`}
            value={values.output_table}
            onChange={(event) => editor.update({ output_table: event.target.value.toLowerCase() })}
          />
        </FieldRow>
      </div>

      <SwitchRow
        checked={values.search_enabled}
        id="search_enabled"
        label={t("adminDashboard.libraries.editor.searchEnabled", "Enable for search")}
        help={t(
          "adminDashboard.libraries.editor.searchEnabledHelp",
          "Allow users to search this library."
        )}
        onCheckedChange={(checked) => editor.update({ search_enabled: checked })}
      />
      <SwitchRow
        checked={values.indexing_enabled}
        id="indexing_enabled"
        label={t("adminDashboard.libraries.editor.indexingEnabled", "Enable for indexing")}
        help={t(
          "adminDashboard.libraries.editor.indexingEnabledHelp",
          "Process this library during indexing runs."
        )}
        onCheckedChange={(checked) => editor.update({ indexing_enabled: checked })}
      />
      <SwitchRow
        checked={values.use_unmodified_previews}
        id="use_unmodified_previews"
        label={t("adminDashboard.libraries.editor.unmodifiedPreviews", "Unmodified previews")}
        help={t(
          "adminDashboard.libraries.editor.unmodifiedPreviewsHelp",
          "Show the original file in previews instead of the processed rendition."
        )}
        onCheckedChange={(checked) => editor.update({ use_unmodified_previews: checked })}
      />

      <FieldRow
        error={errors.chunk_size}
        id="chunk_size"
        label={t("adminDashboard.libraries.editor.chunkSize", "Chunk size")}
        help={t(
          "adminDashboard.libraries.editor.chunkSizeHelp",
          "Maximum characters per chunk when splitting documents. Leave empty for the default (4000)."
        )}
      >
        <Input
          id="chunk_size"
          max={100000}
          min={100}
          placeholder={t("adminDashboard.libraries.editor.chunkSizePlaceholder", "Default: 4000")}
          type="number"
          value={values.chunk_size ?? ""}
          onChange={(event) => {
            const raw = event.target.value
            const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN
            editor.update({ chunk_size: Number.isNaN(parsed) ? null : parsed })
          }}
        />
      </FieldRow>
    </EditorSectionCard>
  )
}
